/* ============================================================
   demo.js — kioskens övningsläge.

   När demoläget är på byter store.js ut fetch-klienten mot den här.
   Varje anrop får samma svar som servern skulle ha gett, men uträknat
   ur den state appen redan har i minnet. Ingenting lämnar surfplattan,
   så laget kan slå in köp, lämna ut gratis till domare och ledare, skriva
   noteringar och registrera svinn utan att en enda rad hamnar i databasen.

   Räknesätten speglar api/src/lib/repo.js med flit: ett demoköp ska
   ge samma summa, samma vinst och samma lageravdrag som ett riktigt.
   ============================================================ */

import { ApiError } from './api.js';
import { isoWeek, priceFor } from './selectors.js';

const uid = () => Math.random().toString(36).slice(2, 10);

const int = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : fallback;
};

/* Kastar som api.js gör, så felbannern och alla try/catch fungerar likadant. */
function fail(message, status = 400) {
  throw new ApiError(message, status);
}

const find = (list, id) => (list || []).find(x => x.id === id);

/* ---------------- kassan ---------------- */

const PAYMENTS = ['paid', 'kontant', 'swish', 'domare', 'ledare'];
const FREE_PAYMENTS = new Set(['domare', 'ledare']);

/**
 * Bokför ett låtsasköp. Priser hämtas ur katalogen precis som på servern,
 * och lagret kontrolleras lika hårt — annars skulle demot inte kunna visa
 * hur det ser ut när något faktiskt tar slut.
 */
function recordSale({ state, session }, { cart, payment = 'paid', ts }) {
  if (!PAYMENTS.includes(payment)) fail('Okänt betalsätt.');
  if (!Array.isArray(cart) || !cart.length) fail('Kassan är tom.');

  const lines = [];
  for (const c of cart) {
    const product = find(state.products, c.id);
    if (!product) fail(`Okänd vara i kassan (${c.id}).`);
    const qty = Math.max(0, int(c.qty));
    if (qty <= 0) continue;
    /* Tillval som ingår — samma regler som servern: högst ett per enhet, ur rätt grupp. */
    const choices = Array.isArray(c.choices) ? c.choices.filter(Boolean).map(String) : [];
    if (choices.length) {
      if (!product.choiceGroupId) fail(`${product.name} har inget tillval.`);
      if (choices.length > qty) fail(`Fler tillval än ${product.name} i kassan.`);
      for (const id of choices) {
        const ch = find(state.products, id);
        if (!ch || ch.groupId !== product.choiceGroupId) fail(`Ogiltigt tillval till ${product.name}.`);
      }
    }
    lines.push({ product, qty, choices: choices.map(id => find(state.products, id)) });
  }
  if (!lines.length) fail('Kassan är tom.');

  const included = [];
  for (const l of lines) {
    for (const ch of l.choices) {
      const hit = included.find(x => x.product.id === ch.id);
      if (hit) hit.qty += 1;
      else included.push({ product: ch, qty: 1, included: true });
    }
  }
  const all = [...lines, ...included];

  /* Varor utan lagersaldo säljs utan att röra lagret — samma regel som skarpt. */
  const untracked = new Set((state.groups || []).filter(g => g.untracked).map(g => g.id));
  const needIng = new Map();
  const needProd = new Map();
  for (const { product, qty } of all) {
    if (untracked.has(product.groupId) || product.untracked) continue;
    if (product.recipe && product.recipe.length) {
      for (const [iid, per] of product.recipe) needIng.set(iid, (needIng.get(iid) || 0) + per * qty);
    } else {
      needProd.set(product.id, (needProd.get(product.id) || 0) + qty);
    }
  }
  for (const [iid, need] of needIng) {
    const ing = find(state.ingredients, iid);
    if (!ing || ing.stock < need) fail(`För lite ${ing ? ing.name : 'lagervara'} i lager.`, 409);
  }
  for (const [pid, need] of needProd) {
    const p = find(state.products, pid);
    if ((p.stock || 0) < need) fail(`Slut i lager: ${p.name}.`, 409);
  }

  const free = FREE_PAYMENTS.has(payment);
  const priceOf = p => priceFor(p, state);
  const costOf = p => (p.recipe && p.recipe.length
    ? p.recipe.reduce((a, [iid, q]) => a + ((find(state.ingredients, iid) || {}).cost || 0) * q, 0)
    : p.cost || 0);

  const items = all.map(({ product, qty, included }) => ({
    productId: product.id, name: product.name, emoji: product.emoji,
    price: free || included ? 0 : priceOf(product), cost: costOf(product), qty,
    ...(included ? { included: true } : {}),
  }));
  const isAdmin = !!(session && session.role === 'admin');

  return {
    id: 's_' + uid(),
    ts: int(ts) || Date.now(),
    items,
    total: items.reduce((a, i) => a + i.price * i.qty, 0),
    profit: items.reduce((a, i) => a + (i.price - i.cost) * i.qty, 0),
    payment,
    by: isAdmin ? 'Admin' : ((session && session.team) || 'Kiosken'),
    manual: isAdmin,
  };
}

/* ---------------- grillen ---------------- */

function bumpGrill(state, { prepared = 0, sold = 0 }) {
  const g = state.grill || { prepared: 0, sold: 0 };
  return {
    prepared: Math.max(0, (g.prepared || 0) + int(prepared)),
    sold: Math.max(0, (g.sold || 0) + int(sold)),
  };
}

/* ---------------- noteringar ---------------- */

function addNote({ session }, { text }) {
  const t = String(text || '').trim();
  if (!t) fail('Noteringen är tom.');
  return {
    id: 'n_' + uid(), text: t, ts: Date.now(), done: false,
    by: (session && session.team) || 'Kiosken',
  };
}

function toggleNote({ state }, _payload, [id]) {
  const note = find(state.notes, id);
  if (!note) fail('Noteringen finns inte.', 404);
  return { ...note, done: !note.done };
}

/* ---------------- svinn ---------------- */

function addWaste({ state }, { lines, note }) {
  const picked = (Array.isArray(lines) ? lines : []).filter(l => int(l.qty) > 0);
  if (!picked.length) fail('Inget svinn valt.');

  let value = 0;
  const recorded = [];
  for (const l of picked) {
    const qty = int(l.qty);
    const src = l.kind === 'ing' ? find(state.ingredients, l.id) : find(state.products, l.id);
    if (!src) fail(`Okänd rad i svinnet (${l.id}).`);
    value += (src.cost || 0) * qty;
    recorded.push({ kind: l.kind, id: src.id, name: src.name, qty });
  }

  const ts = Date.now();
  const now = new Date(ts);
  const week = isoWeek(now);
  const year = now.getFullYear();
  const team = ((state.schedule || []).find(s => s.week === week && s.year === year) || {}).team || '';

  return {
    id: 'w_' + uid(), ts, week, year, team,
    note: String(note || '').trim(),
    lines: recorded,
    qty: recorded.reduce((a, l) => a + l.qty, 0),
    value,
  };
}

/* ---------------- routing ---------------- */

/* Bara det kiosken själv kan trycka på. Allt annat stoppas hellre med ett
   tydligt fel än släpps igenom till databasen bakom ryggen på demot. */
const ROUTES = [
  ['POST', /^\/sales$/, recordSale],
  ['POST', /^\/grill\/prepared$/, (ctx, p) => bumpGrill(ctx.state, { prepared: int(p.qty, 1) })],
  ['POST', /^\/grill\/reset$/, () => ({ prepared: 0, sold: 0 })],
  ['POST', /^\/notes$/, addNote],
  ['PATCH', /^\/notes\/([^/?]+)$/, toggleNote],
  ['DELETE', /^\/notes\/([^/?]+)$/, () => ({ ok: true })],
  ['POST', /^\/waste$/, addWaste],
];

/**
 * En klient med samma yta som `api`, fast utan nätverk.
 * `getState` och `getSession` läses vid varje anrop så svaren bygger på
 * det demot hunnit göra, inte på hur det såg ut när demot startades.
 */
export function createDemoClient({ getState, getSession }) {
  const call = (method, path, payload = {}) => {
    for (const [m, re, fn] of ROUTES) {
      if (m !== method) continue;
      const match = re.exec(path);
      if (!match) continue;
      /* Synkront resultat i ett löfte — anroparna väntar på ett svar. */
      return Promise.resolve()
        .then(() => fn({ state: getState(), session: getSession() }, payload, match.slice(1)));
    }
    return Promise.reject(new ApiError('Det här går inte att göra i demoläget.', 405));
  };

  return {
    get: path => call('GET', path),
    post: (path, payload) => call('POST', path, payload),
    patch: (path, payload) => call('PATCH', path, payload),
    put: (path, payload) => call('PUT', path, payload),
    del: path => call('DELETE', path),
  };
}
